/**
 * QrTagsPage – Verwaltung der standortgebundenen QR-Code-Tags (Phase 8)
 *
 * Features:
 *  – Info-Box mit Sicherheitskonzept-Erklärung
 *  – Toggle: QR-Scan vor dem Stempeln erforderlich
 *  – Gefilterte Tag-Liste (aktiv/inaktiv, offen/eingeschränkt)
 *  – Aktionen: Bearbeiten | Code rotieren | Deaktivieren
 *  – Tag-Dialog: Neu / Bearbeiten
 *  – QR-Code-Anzeige-Modal nach Erstellen / Rotieren (mit Drucken-Button)
 */

import { Fragment, useState, useEffect, useCallback, useRef } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import {
  QrCode,
  ShieldCheck,
  RotateCcw,
  Pencil,
  PowerOff,
  Plus,
  MapPin,
  Lock,
  Unlock,
  Printer,
  X,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formCard, pageTitle, sectionTitle, fieldLabel } from '@/lib/form-styles'
import { Modal } from '@symphenia/ui-components'
import { api } from '@/lib/axios'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { Feldgruppe, FeldStatus, FeldStatusZeichen, SchalterZeile, feldKlasse, mitStand, useFeld, useSpeicherstelle, useStellenDaten, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { useZeitKonfiguration } from '@/lib/zeit-konfiguration'
import { usersApi } from '@/lib/users/users.api'
import type { User } from '@/lib/users/user.types'
import { bestaetige } from '@/lib/dialoge'

// ─── Typen ─────────────────────────────────────────────────────────────────────

interface QrTag {
  id: string
  uid: string
  name: string
  locationName: string
  gpsLat: number | null
  gpsLng: number | null
  isActive: boolean
  restricted: boolean
  createdAt: string
  _count?: { users: number }
}

interface AssignedUser {
  id: string
  email: string
  profile: { firstName: string | null; lastName: string | null } | null
}

interface QrTagForm {
  name: string
  locationName: string
  gpsLat: string
  gpsLng: string
  restricted: boolean
}

const EMPTY_FORM: QrTagForm = {
  name: '',
  locationName: '',
  gpsLat: '',
  gpsLng: '',
  restricted: false,
}

// ─── QR-Code-Anzeige-Modal ─────────────────────────────────────────────────────

/** Text für das Druckfenster — Name und Standort sind Eingaben und dürfen kein HTML werden. */
function html(text: string): string {
  return text.replace(/[&<>"']/g, (z) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[z]!)
}

function QrCodeModal({ tag, onClose }: { tag: QrTag; onClose: () => void }) {
  const qrUrl = `symphenia:qr:${tag.uid}`
  const codeRef = useRef<HTMLDivElement>(null)

  // KA-47: Gedruckt wird der Code, der hier schon zu sehen ist (SVG aus qrcode.react) — ohne
  // Bibliothek aus dem Internet. Vorher lud das Druckfenster qrcode.js vom CDN und blieb offline leer.
  const handlePrint = () => {
    const svg = codeRef.current?.innerHTML ?? ''
    const printWin = window.open('', '_blank', 'width=400,height=500')
    if (!printWin) return
    printWin.document.write(`
      <html><head><title>QR-Code – ${html(tag.name)}</title>
      <style>
        body { font-family: sans-serif; text-align: center; padding: 20px; }
        h2 { font-size: 18px; margin-bottom: 4px; }
        p { color: #666; font-size: 13px; margin: 4px 0; }
        svg { width: 220px; height: 220px; margin: 16px auto; display: block; }
        .uid { font-family: monospace; font-size: 10px; color: #999; margin-top: 8px; word-break: break-all; }
      </style></head><body>
        <h2>${html(tag.name)}</h2>
        <p>${html(tag.locationName)}</p>
        ${svg}
        <p class="uid">${html(tag.uid)}</p>
      </body></html>
    `)
    printWin.document.close()
    printWin.focus()
    printWin.print()
    printWin.close()
  }

  return (
    <Modal isOpen onClose={onClose} size="sm" className="max-w-sm" padded={false} hideCloseButton>
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary" />
            <span className="font-semibold text-foreground">{tag.name}</span>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 text-center">
          <p className="text-sm text-muted-foreground mb-4">{tag.locationName}</p>
          {/* KA-47: der scanbare Code — weißer Grund auch im dunklen Design, sonst liest ihn keine Kamera */}
          <div ref={codeRef} className="mx-auto w-48 h-48 bg-white border border-border rounded-xl flex items-center justify-center p-3">
            <QRCodeSVG value={qrUrl} size={168} level="M" marginSize={0} title={`QR-Code ${tag.name}`} />
          </div>
          <p className="mt-3 text-xs text-muted-foreground font-mono break-all">{tag.uid}</p>
        </div>

        <div className="px-5 pb-5 flex gap-3">
          <Button variant="outline" className="flex-1 gap-2" onClick={handlePrint}>
            <Printer className="w-4 h-4" />
            Drucken
          </Button>
          <Button className="flex-1" onClick={onClose}>
            Schließen
          </Button>
        </div>
    </Modal>
  )
}

// ─── Mitarbeiter-Verwaltungs-Dialog ────────────────────────────────────────────

function UserManageDialog({
  tag,
  onClose,
  onChanged,
}: {
  tag: QrTag
  onClose: () => void
  onChanged: () => void
}) {
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([])
  const [allUsers, setAllUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [assignedRes, usersRes] = await Promise.all([
        api.get<{ data: AssignedUser[] }>(`/qr-tags/${tag.id}/users`),
        usersApi.list({ limit: 100, kind: 'EMPLOYEE' }),
      ])
      setAssignedUsers(assignedRes.data.data)
      setAllUsers(usersRes.data.data)
    } catch {
      setError('Daten konnten nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [tag.id])

  useEffect(() => { void loadData() }, [loadData])

  const assignedIds = new Set(assignedUsers.map((u) => u.id))
  const unassignedUsers = allUsers.filter((u) => u.isActive && !assignedIds.has(u.id))

  function userDisplayName(u: AssignedUser | User) {
    const firstName = u.profile?.firstName
    const lastName = u.profile?.lastName
    if (firstName || lastName) return `${firstName ?? ''} ${lastName ?? ''}`.trim()
    return u.email
  }

  async function handleAdd() {
    if (!selectedUserId) return
    setAdding(true); setError(null)
    try {
      await api.post(`/qr-tags/${tag.id}/users/${selectedUserId}`)
      setSelectedUserId('')
      await loadData()
      onChanged()
    } catch {
      setError('Mitarbeiter konnte nicht hinzugefügt werden.')
    } finally {
      setAdding(false)
    }
  }

  async function handleRemove(userId: string) {
    setRemoving(userId); setError(null)
    try {
      await api.delete(`/qr-tags/${tag.id}/users/${userId}`)
      await loadData()
      onChanged()
    } catch {
      setError('Zuweisung konnte nicht entfernt werden.')
    } finally {
      setRemoving(null)
    }
  }

  return (
    <Modal isOpen onClose={onClose} size="md" className="max-w-md" padded={false} scrollBody={false} hideCloseButton>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            <span className="font-semibold text-foreground">Mitarbeiter — {tag.name}</span>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Hinweis-Box */}
        <div className="mx-5 mt-4 rounded-lg border border-amber-500 bg-amber-100 dark:bg-amber-950 px-4 py-3 text-xs text-amber-900 dark:text-amber-100 shrink-0">
          <p className="font-bold mb-1">Wer diesen Code scannen darf</p>
          <p>Die Zuweisung gilt nur bei <strong>eingeschränktem Zugriff</strong>: Dann dürfen ausschließlich die zugewiesenen Mitarbeiter diesen Code scannen. Einen offenen Code dürfen alle scannen.</p>
          <p className="mt-1">Ob ein Mitarbeiter überhaupt scannen muss, stellst du unter <strong>„Stundensätze je Mitarbeiter"</strong> in der Spalte <strong>„QR-Pflicht"</strong> ein.</p>
        </div>

        {/* Fehler */}
        {error && (
          <div className="mx-5 mt-3 rounded-lg bg-destructive/10 border border-destructive/30 px-3 py-2 text-sm text-destructive shrink-0">
            {error}
          </div>
        )}

        {/* Inhalt */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-4">Laden…</p>
          ) : (
            <div className="space-y-4">
              {/* Zugewiesene Mitarbeiter */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Zugewiesen ({assignedUsers.length})
                </p>
                {assignedUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    {tag.restricted ? 'Keine Zuweisung — niemand darf diesen Code scannen' : 'Keine Zuweisung — der Code ist offen, alle dürfen ihn scannen'}
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {assignedUsers.map((u) => (
                      <li key={u.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{userDisplayName(u)}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                        <button
                          onClick={() => handleRemove(u.id)}
                          disabled={removing === u.id}
                          className="shrink-0 p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 transition-colors disabled:opacity-50"
                          title="Zuweisung entfernen"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Mitarbeiter hinzufügen */}
              {unassignedUsers.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Mitarbeiter hinzufügen
                  </p>
                  <div className="flex gap-2">
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Mitarbeiter wählen…</option>
                      {unassignedUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {userDisplayName(u)} ({u.email})
                        </option>
                      ))}
                    </select>
                    <Button
                      onClick={handleAdd}
                      disabled={!selectedUserId || adding}
                      className="shrink-0"
                    >
                      {adding ? '…' : <Plus className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 shrink-0">
          <Button variant="outline" className="w-full" onClick={onClose}>
            Schließen
          </Button>
        </div>
    </Modal>
  )
}

// ─── Haupt-Komponente ──────────────────────────────────────────────────────────

export function QrTagsPage() {
  // ── State ────────────────────────────────────────────────────────────────────
  const [tags, setTags] = useState<QrTag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const hasModule = useAuthStore((st) => st.hasModule)
  const rechte = useAuthStore((st) => st.user?.permissions)
  const darfKonfig = erfuelltRecht('time.config.manage', rechte) && hasModule('TIME_TRACKING.SCAN')
  // Aufgeklappte Karte eines Tags (Bearbeiten ohne Dialog, AP-36 Fall 1)
  const [offenId, setOffenId] = useState<string | null>(null)

  // Mitarbeiter-Zuweisung
  const [userPanel, setUserPanel] = useState<QrTag | null>(null)

  // Filter
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('active')
  const [filterRestricted, setFilterRestricted] = useState<'all' | 'open' | 'restricted'>('all')

  // Dialog: nur noch fürs Anlegen (Fall 2)
  const [showDialog, setShowDialog] = useState(false)
  const [form, setForm] = useState<QrTagForm>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [qrModalTag, setQrModalTag] = useState<QrTag | null>(null)
  const [rotating, setRotating] = useState<string | null>(null)
  const [deactivating, setDeactivating] = useState<string | null>(null)

  // ── Daten laden ──────────────────────────────────────────────────────────────

  const fetchTags = useCallback(async () => {
    try {
      setLoading(true); setError(null)
      // AP-36: Die QR-Pflicht lädt ihre eigene Karte (nur mit Verwaltungsrecht) —
      // `GET /time-config` hier riss für Leser mit 403 die ganze Seite ab.
      const tagsRes = await api.get<{ data: QrTag[] }>('/qr-tags')
      setTags(tagsRes.data.data)
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e?.response?.data?.message ?? 'Laden fehlgeschlagen.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetchTags() }, [fetchTags])

  // ── Filter ───────────────────────────────────────────────────────────────────

  const filteredTags = tags.filter((t) => {
    if (filterActive === 'active' && !t.isActive) return false
    if (filterActive === 'inactive' && t.isActive) return false
    if (filterRestricted === 'open' && t.restricted) return false
    if (filterRestricted === 'restricted' && !t.restricted) return false
    return true
  })

  // ── Dialog: Neu / Bearbeiten ─────────────────────────────────────────────────

  const openNew = () => {
    setForm(EMPTY_FORM)
    setFormError(null)
    setShowDialog(true)
  }

  /** Die Karte meldet den gespeicherten Stand — die Zeile darüber zeigt ihn. */
  const tagGeaendert = useCallback((neu: QrTag) => {
    setTags((prev) => prev.map((t) => (t.id === neu.id ? { ...t, ...neu } : t)))
  }, [])

  const handleSave = async () => {
    if (!form.name.trim() || !form.locationName.trim()) {
      setFormError('Name und Standort sind Pflichtfelder.')
      return
    }
    setSaving(true); setFormError(null)
    try {
      const payload = {
        name: form.name.trim(),
        locationName: form.locationName.trim(),
        gpsLat: form.gpsLat ? parseFloat(form.gpsLat) : undefined,
        gpsLng: form.gpsLng ? parseFloat(form.gpsLng) : undefined,
        restricted: form.restricted,
      }
      const res = await api.post<{ data: QrTag }>('/qr-tags', payload)
      setShowDialog(false)
      setQrModalTag(res.data.data)
      void fetchTags()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } }
      setFormError(e?.response?.data?.message ?? 'Speichern fehlgeschlagen.')
    } finally {
      setSaving(false)
    }
  }

  // ── Code rotieren ─────────────────────────────────────────────────────────────

  const handleRotate = async (tag: QrTag) => {
    if (!(await bestaetige(`Code für „${tag.name}" rotieren? Der alte Aufkleber wird sofort ungültig.`))) return
    setRotating(tag.id)
    try {
      const res = await api.post<{ data: QrTag }>(`/qr-tags/${tag.id}/rotate`)
      setQrModalTag(res.data.data)
      void fetchTags()
    } catch {
      setError('Code-Rotation fehlgeschlagen.')
    } finally {
      setRotating(null)
    }
  }

  // ── Deaktivieren ──────────────────────────────────────────────────────────────

  const handleDeactivate = async (tag: QrTag) => {
    if (!(await bestaetige(`QR-Tag „${tag.name}" deaktivieren?`))) return
    setDeactivating(tag.id)
    try {
      await api.delete(`/qr-tags/${tag.id}`)
      void fetchTags()
    } catch {
      setError('Deaktivieren fehlgeschlagen.')
    } finally {
      setDeactivating(null)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 p-6">
      <h1 className={pageTitle}>QR-Codes & Standorte</h1>

      {/* ── Info-Box Sicherheitskonzept ─────────────────────────────────────── */}
      <div className="rounded-xl border border-primary/30 bg-card p-5 flex gap-4">
        <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="space-y-1.5 text-sm">
          <p className="font-semibold text-foreground">Sicherheitskonzept</p>
          <p className="text-foreground">
            Jeder QR-Code enthält eine eindeutige, vom System generierte ID. Mitarbeiter müssen diesen Code
            vor dem Stempeln scannen – das System prüft, ob der Code gültig ist und ob der Mitarbeiter für
            diesen Standort berechtigt ist. Das Prüf-Token ist nur <strong>60 Sekunden gültig</strong>. Zusätzlich
            wird der GPS-Standort mit den hinterlegten Koordinaten abgeglichen.
          </p>
          <p className="text-foreground">
            <strong>Bei Verdacht auf Missbrauch:</strong> „Code rotieren" klicken, neuen Code ausdrucken,
            Aufkleber tauschen. Wer den alten Code fotografiert hat, kann ihn sofort nicht mehr verwenden.
            Fehlgeschlagene Versuche sind im Buchungsprotokoll sichtbar.
          </p>
        </div>
      </div>

      {/* ── Einstellung: qrRequired (nur mit Verwaltungsrecht und Scan-Baustein) ── */}
      {darfKonfig && <QrPflicht />}

      {/* ── Filter + Aktionsleiste ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-border overflow-hidden text-sm">
          {(['all', 'active', 'inactive'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setFilterActive(v)}
              className={`px-3 py-1.5 transition-colors ${
                filterActive === v
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              {v === 'all' ? 'Alle' : v === 'active' ? 'Aktiv' : 'Inaktiv'}
            </button>
          ))}
        </div>
        <div className="flex rounded-lg border border-border overflow-hidden text-sm">
          {(['all', 'open', 'restricted'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setFilterRestricted(v)}
              className={`px-3 py-1.5 transition-colors ${
                filterRestricted === v
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              {v === 'all' ? 'Modus: Alle' : v === 'open' ? 'Offen' : 'Eingeschränkt'}
            </button>
          ))}
        </div>
        <Button className="ml-auto gap-2" onClick={openNew}>
          <Plus className="w-4 h-4" />
          Neuer Tag
        </Button>
      </div>

      {/* ── Fehler ──────────────────────────────────────────────────────────── */}
      {error && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* ── Tabelle ─────────────────────────────────────────────────────────── */}
      <div className={formCard + ' overflow-x-auto'}>
        {loading ? (
          <p className="text-muted-foreground text-sm py-4 text-center">Lade Tags…</p>
        ) : filteredTags.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4 text-center">Keine QR-Tags gefunden.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground text-xs">
                <th className="text-left py-2 pr-4 font-medium">Name</th>
                <th className="text-left py-2 pr-4 font-medium">Standort</th>
                <th className="text-left py-2 pr-4 font-medium">GPS</th>
                <th className="text-left py-2 pr-4 font-medium">Modus</th>
                <th className="text-left py-2 pr-4 font-medium">Status</th>
                <th className="text-left py-2 pr-4 font-medium">Mitarbeiter</th>
                <th className="text-right py-2 font-medium">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredTags.map((tag) => (
                <Fragment key={tag.id}>
                <tr className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                  <td className="py-3 pr-4 font-medium text-foreground">{tag.name}</td>
                  <td className="py-3 pr-4 text-muted-foreground">{tag.locationName}</td>
                  <td className="py-3 pr-4">
                    {tag.gpsLat != null ? (
                      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                        <MapPin className="w-3 h-3" />
                        <span className="text-xs">{tag.gpsLat.toFixed(4)}, {tag.gpsLng?.toFixed(4)}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground/50 text-xs">—</span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    {tag.restricted ? (
                      <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 text-xs">
                        <Lock className="w-3 h-3" />
                        Eingeschränkt
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground text-xs">
                        <Unlock className="w-3 h-3" />
                        Offen
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      tag.isActive
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {tag.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    {tag.restricted && tag._count ? (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Users className="w-3 h-3" />
                        {tag._count.users}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/50">—</span>
                    )}
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* QR anzeigen */}
                      <button
                        onClick={() => setQrModalTag(tag)}
                        className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        title="QR-Code anzeigen"
                      >
                        <QrCode className="w-4 h-4" />
                      </button>
                      {/* Mitarbeiter verwalten */}
                      <button
                        onClick={() => setUserPanel(tag)}
                        className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        title="Mitarbeiter-Zuweisung verwalten"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      {/* Bearbeiten */}
                      <button
                        onClick={() => setOffenId(offenId === tag.id ? null : tag.id)}
                        className={`p-1.5 rounded hover:bg-muted transition-colors ${offenId === tag.id ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        title={offenId === tag.id ? 'Zuklappen' : 'Bearbeiten'}
                        aria-expanded={offenId === tag.id}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {/* Rotieren */}
                      {tag.isActive && (
                        <button
                          onClick={() => handleRotate(tag)}
                          disabled={rotating === tag.id}
                          className="p-1.5 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600 dark:text-amber-400 transition-colors disabled:opacity-50"
                          title="Code rotieren (Sicherheitsrotation)"
                        >
                          <RotateCcw className={`w-4 h-4 ${rotating === tag.id ? 'animate-spin' : ''}`} />
                        </button>
                      )}
                      {/* Deaktivieren */}
                      {tag.isActive && (
                        <button
                          onClick={() => handleDeactivate(tag)}
                          disabled={deactivating === tag.id}
                          className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 transition-colors disabled:opacity-50"
                          title="Deaktivieren"
                        >
                          <PowerOff className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {offenId === tag.id && (
                  <tr className="border-b border-border/50">
                    <td colSpan={7} className="px-2 py-3">
                      <TagKarte tag={tag} onGeaendert={tagGeaendert} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Tag-Dialog ──────────────────────────────────────────────────────── */}
      {showDialog && (
        <Modal isOpen onClose={() => setShowDialog(false)} size="md" className="max-w-md" padded={false} hideCloseButton>
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
              <h2 className="font-semibold text-foreground">
                Neuen QR-Tag anlegen
              </h2>
              <button onClick={() => setShowDialog(false)} className="text-muted-foreground hover:text-foreground p-1 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className={fieldLabel}>Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="z.B. Eingang Halle B"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className={fieldLabel}>Standortname (für Mitarbeiter) *</label>
                <input
                  type="text"
                  value={form.locationName}
                  onChange={(e) => setForm((f) => ({ ...f, locationName: e.target.value }))}
                  placeholder="z.B. Haupteingang – Halle B, Erdgeschoss"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={fieldLabel}>GPS Breitengrad</label>
                  <input
                    type="number"
                    step="any"
                    value={form.gpsLat}
                    onChange={(e) => setForm((f) => ({ ...f, gpsLat: e.target.value }))}
                    placeholder="48.2082"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className={fieldLabel}>GPS Längengrad</label>
                  <input
                    type="number"
                    step="any"
                    value={form.gpsLng}
                    onChange={(e) => setForm((f) => ({ ...f, gpsLng: e.target.value }))}
                    placeholder="16.3738"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, restricted: !f.restricted }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    form.restricted ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    form.restricted ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <p className="text-sm text-foreground">Eingeschränkter Zugriff</p>
                  <p className="text-xs text-muted-foreground">Nur zugewiesene Mitarbeiter dürfen diesen Code scannen</p>
                </div>
              </div>

              {formError && (
                <p className="text-sm text-destructive">{formError}</p>
              )}
            </div>

            <div className="px-5 pb-5 flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowDialog(false)}>
                Abbrechen
              </Button>
              <Button className="flex-1" onClick={handleSave} disabled={saving}>
                {saving ? 'Wird angelegt…' : 'Anlegen'}
              </Button>
            </div>
        </Modal>
      )}

      {/* ── QR-Code-Modal ───────────────────────────────────────────────────── */}
      {qrModalTag && (
        <QrCodeModal tag={qrModalTag} onClose={() => setQrModalTag(null)} />
      )}

      {/* ── Mitarbeiter-Zuweisung ────────────────────────────────────────────── */}
      {userPanel && (
        <UserManageDialog
          tag={userPanel}
          onClose={() => setUserPanel(null)}
          onChanged={() => void fetchTags()}
        />
      )}
    </div>
  )
}

// ── QR-Pflicht (Einstellung der Zeiterfassung) ───────────────────────────────

/**
 * Derselbe Schalter wie in den Grundeinstellungen, über dieselbe Speicherstelle
 * (`timeConfig`). Abschalten nimmt den Bypass als Folgefeld mit (3.1).
 */
function QrPflicht() {
  const { stelle, geladen } = useZeitKonfiguration()
  if (!geladen) return null
  return <QrPflichtSchalter stelle={stelle as Speicherstelle<unknown>} />
}

function QrPflichtSchalter({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const qr = useFeld<boolean>(stelle, 'qrRequired', {
    art: 'schalter',
    label: 'QR-Scan vor dem Stempeln erforderlich',
    folge: (an) => (an ? {} : { qrAllowBypass: false }),
  })
  return (
    <div className={formCard}>
      <h2 className={sectionTitle}>Einstellung</h2>
      <SchalterZeile
        feld={qr}
        label="QR-Scan vor dem Stempeln erforderlich"
        hinweis={'Wenn aktiv, müssen Mitarbeiter vor dem Einstempeln einen gültigen QR-Code scannen — alle, bei denen unter „Stundensätze je Mitarbeiter" die „QR-Pflicht" angehakt ist (Vorgabe).'}
      />
    </div>
  )
}

// ── Karte eines Tags: Bearbeiten ohne Dialog ─────────────────────────────────

/**
 * AP-36 Fall 1: Die Felder eines Tags speichern beim Verlassen; die GPS-Koordinaten
 * sind eine Gruppe (nur zusammen sinnvoll). Drehen und Deaktivieren bleiben
 * Handlungen in der Zeile.
 */
function TagKarte({ tag, onGeaendert }: { tag: QrTag; onGeaendert: (t: QrTag) => void }) {
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `qrTag:${tag.id}`,
    bezeichnung: `QR-Tag „${tag.name}"`,
    daten: { data: tag } as unknown as Datensatz,
    schreibe: (patch, stand) => api.put<Datensatz>(`/qr-tags/${tag.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/qr-tags/${tag.id}`, method: 'PUT' },
  }) as Speicherstelle<unknown>
  const daten = useStellenDaten(stelle)
  useEffect(() => {
    if (daten.id) onGeaendert(daten as unknown as QrTag)
  }, [daten, onGeaendert])

  const name = useFeld<string | null>(stelle, 'name', { label: 'Name', pflicht: true })
  const ort = useFeld<string | null>(stelle, 'locationName', { label: 'Standortname', pflicht: true })
  const eingeschraenkt = useFeld<boolean>(stelle, 'restricted', { art: 'schalter', label: 'Eingeschränkter Zugriff' })

  return (
    <div className="grid gap-4 rounded-lg border border-border bg-background p-4 sm:grid-cols-2">
      <label className="space-y-1">
        <span className={`${fieldLabel} flex items-center gap-1.5`}>Name * <FeldStatusZeichen status={name} /></span>
        <input className={feldKlasse} placeholder="z.B. Eingang Halle B" {...name.props} />
        <FeldStatus status={name} zeichen={false} />
      </label>
      <label className="space-y-1">
        <span className={`${fieldLabel} flex items-center gap-1.5`}>Standortname (für Mitarbeiter) * <FeldStatusZeichen status={ort} /></span>
        <input className={feldKlasse} placeholder="z.B. Haupteingang – Halle B, Erdgeschoss" {...ort.props} />
        <FeldStatus status={ort} zeichen={false} />
      </label>
      <Feldgruppe
        stelle={stelle}
        label="GPS-Koordinaten"
        className="grid grid-cols-2 gap-3 sm:col-span-2"
        pruefe={(w) => ((w.gpsLat == null) !== (w.gpsLng == null) ? 'Breiten- und Längengrad gehören zusammen.' : null)}
        status={(st) => (
          <div className="col-span-2">
            <FeldStatus status={st} />
          </div>
        )}
      >
        <GpsFeld stelle={stelle} feld="gpsLat" label="GPS Breitengrad" platzhalter="48.2082" grenze={90} />
        <GpsFeld stelle={stelle} feld="gpsLng" label="GPS Längengrad" platzhalter="16.3738" grenze={180} />
      </Feldgruppe>
      <div className="sm:col-span-2">
        <SchalterZeile feld={eingeschraenkt} label="Eingeschränkter Zugriff" hinweis="Nur zugewiesene Mitarbeiter dürfen diesen Code scannen" />
      </div>
    </div>
  )
}

function GpsFeld({ stelle, feld, label, platzhalter, grenze }: { stelle: Speicherstelle<unknown>; feld: string; label: string; platzhalter: string; grenze: number }) {
  const f = useFeld<number | null>(stelle, feld, {
    art: 'zahl',
    stellen: 7,
    label,
    pruefe: (w) => (w !== null && Math.abs(w) > grenze ? `Zwischen −${grenze} und ${grenze}.` : null),
  })
  return (
    <label className="space-y-1">
      <span className={fieldLabel}>{label}</span>
      <input inputMode="decimal" className={feldKlasse} placeholder={platzhalter} {...f.props} />
      <FeldStatus status={f} zeichen={false} />
    </label>
  )
}
